import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformBien } from "@/store/platform/types";
import type { Bien, BienInput, BienStatut, BienType } from "./types";

interface BiensContextValue {
  biens: Bien[];
  addBien: (input: BienInput) => Bien;
  getBien: (id: string) => Bien | undefined;
  removeBien: (id: string) => void;
  updateBien: (id: string, input: BienInput) => void;
  countByImmeuble: (immeubleId: string) => number;
  stats: { total: number; occupes: number; disponibles: number };
}

const BiensContext = createContext<BiensContextValue | null>(null);

function toOwnerBien(source: PlatformBien): Bien {
  return {
    id: source.id,
    type: source.type as BienType,
    reference: source.reference,
    immeubleId: source.immeubleId,
    etage: source.etage,
    numero: source.numero,
    surface: source.surface,
    loyerMensuel: source.loyerMensuel,
    statut: source.statut as BienStatut,
    description: source.description,
    infosComplementaires: source.infosComplementaires,
    createdAt: source.createdAt,
  };
}

/**
 * Vue « Propriétaire » du store unifié de la plateforme (scope « proprietaire »).
 */
export function BiensProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const biens = useMemo(
    () => platform.biens.filter((item) => item.scope === "proprietaire").map(toOwnerBien),
    [platform.biens]
  );

  const addBien = useCallback(
    (input: BienInput) => {
      const created = platform.addBien({ ...input, scope: "proprietaire", proprietaireId: "" });
      return toOwnerBien(created);
    },
    [platform.addBien]
  );

  const updateBien = useCallback(
    (id: string, input: BienInput) => {
      platform.updateBien(id, { ...input, scope: "proprietaire", proprietaireId: "" });
    },
    [platform.updateBien]
  );

  const removeBien = useCallback(
    (id: string) => {
      platform.removeBien(id);
    },
    [platform.removeBien]
  );

  const getBien = useCallback((id: string) => biens.find((item) => item.id === id), [biens]);

  const countByImmeuble = useCallback(
    (immeubleId: string) => biens.filter((item) => item.immeubleId === immeubleId).length,
    [biens]
  );

  const stats = useMemo(
    () => ({
      total: biens.length,
      occupes: biens.filter((item) => item.statut === "occupe").length,
      disponibles: biens.filter((item) => item.statut === "disponible").length,
    }),
    [biens]
  );

  const value = useMemo(
    () => ({ biens, addBien, getBien, removeBien, updateBien, countByImmeuble, stats }),
    [biens, addBien, getBien, removeBien, updateBien, countByImmeuble, stats]
  );

  return <BiensContext.Provider value={value}>{children}</BiensContext.Provider>;
}

export function useBiens(): BiensContextValue {
  const context = useContext(BiensContext);
  if (!context) {
    throw new Error("useBiens doit être utilisé à l'intérieur de BiensProvider");
  }
  return context;
}